/**
 * Rules for the forms that shape a site: the site itself, its storage locations and its bins.
 * Pure, so the checks the server also makes can be tested without rendering. The server stays
 * the authority (scope, duplicates across users); these only spare a round trip.
 */
import type { BinFormInput, SiteFormInput, StorageLocationFormInput } from '../../../services/locationsService';
import type { SiteWithLocations, StorageBin, StorageLocation } from '../types';
import { BIN_CODE_MAX_LENGTH } from './addBin';
import { locationKindLabel } from './siteDrillThrough';

/** Adding and editing sites, storage locations and bins. Same key as the server's routes. */
export const LOCATIONS_MANAGE_PERMISSION = 'materials:locations_manage';
/** Why those controls are switched off. */
export const STRUCTURE_NO_PERMISSION_REASON = 'You do not have permission to manage locations and bins';

/** Mirror the server's limits (locations.input.ts). */
export const SITE_NAME_MAX_LENGTH = 120;
export const SITE_NUMBER_MAX_LENGTH = 20;
export const DESCRIPTION_MAX_LENGTH = 500;
export const LOCATION_NAME_MAX_LENGTH = 80;

/** Who a site or bin can belong to. "Unknown" is what an unset owner reads as, not a choice. */
export const OWNER_CHOICES = ['DRM', 'MoTI', 'Private / Third-Party'] as const;

/** StorageLocationType, in the order a yard is usually walked. */
export const LOCATION_KINDS = ['STOCKPILE', 'SHED', 'FUEL_TANK', 'BLADDER', 'RACK', 'CAGE', 'SEA_CAN', 'STORE_ROOM', 'YARD_AREA'].map(
  (value) => ({ value, label: locationKindLabel(value) })
);

const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** A number field: blank is "not set", anything else must parse. */
function parseNumber(raw: string): number | null | 'invalid' {
  const text = raw.trim();
  if (!text) return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : 'invalid';
}

const textOrNull = (raw: string) => raw.trim() || null;

// --- site ----------------------------------------------------------------------------------

export interface SiteForm {
  name: string;
  number: string;
  type: 'pit' | 'yard';
  owner: string;
  serviceAreaId: string;
  foremanAreaIds: string[];
  latitude: string;
  longitude: string;
  description: string;
}

export type SiteFormErrors = Partial<Record<keyof SiteForm, string>>;

export function siteFormFrom(site: SiteWithLocations | null): SiteForm {
  if (!site) {
    return { name: '', number: '', type: 'yard', owner: 'DRM', serviceAreaId: '', foremanAreaIds: [], latitude: '', longitude: '', description: '' };
  }
  return {
    name: site.name,
    number: site.number ?? '',
    type: site.type === 'pit' ? 'pit' : 'yard',
    owner: site.owner === 'Unknown' ? '' : site.owner,
    serviceAreaId: site.serviceAreaId,
    foremanAreaIds: site.foremanAreaIds,
    latitude: site.latitude === null ? '' : String(site.latitude),
    longitude: site.longitude === null ? '' : String(site.longitude),
    description: site.description ?? '',
  };
}

export function validateSiteForm(form: SiteForm): SiteFormErrors {
  const errors: SiteFormErrors = {};
  if (!form.name.trim()) errors.name = 'Enter a site name';
  else if (form.name.trim().length > SITE_NAME_MAX_LENGTH) errors.name = `Use ${SITE_NAME_MAX_LENGTH} characters or fewer`;
  if (form.number.trim().length > SITE_NUMBER_MAX_LENGTH) errors.number = `Use ${SITE_NUMBER_MAX_LENGTH} characters or fewer`;
  if (!form.serviceAreaId) errors.serviceAreaId = 'Choose a service area';
  if (form.description.trim().length > DESCRIPTION_MAX_LENGTH) errors.description = `Use ${DESCRIPTION_MAX_LENGTH} characters or fewer`;

  const latitude = parseNumber(form.latitude);
  const longitude = parseNumber(form.longitude);
  if (latitude === 'invalid' || (latitude !== null && (latitude < -90 || latitude > 90))) {
    errors.latitude = 'A latitude between -90 and 90';
  }
  if (longitude === 'invalid' || (longitude !== null && (longitude < -180 || longitude > 180))) {
    errors.longitude = 'A longitude between -180 and 180';
  }
  if (!errors.latitude && !errors.longitude && (latitude === null) !== (longitude === null)) {
    errors[latitude === null ? 'latitude' : 'longitude'] = 'Enter both, or neither';
  }
  return errors;
}

/** Only call once validateSiteForm is clean. */
export function toSiteInput(form: SiteForm): SiteFormInput {
  return {
    name: form.name.trim(),
    number: textOrNull(form.number),
    type: form.type,
    owner: textOrNull(form.owner),
    serviceAreaId: form.serviceAreaId,
    foremanAreaIds: form.foremanAreaIds,
    latitude: parseNumber(form.latitude) as number | null,
    longitude: parseNumber(form.longitude) as number | null,
    description: textOrNull(form.description),
  };
}

// --- storage location -----------------------------------------------------------------------

export interface LocationForm {
  name: string;
  kind: string;
}

export type LocationFormErrors = Partial<Record<keyof LocationForm, string>>;

export function validateLocationForm(
  form: LocationForm,
  siblings: Pick<StorageLocation, 'id' | 'name'>[],
  siteName: string,
  editingId: string | null
): LocationFormErrors {
  const errors: LocationFormErrors = {};
  const name = form.name.trim();
  if (!name) errors.name = 'Enter a location name';
  else if (name.length > LOCATION_NAME_MAX_LENGTH) errors.name = `Use ${LOCATION_NAME_MAX_LENGTH} characters or fewer`;
  else if (siblings.some((location) => location.id !== editingId && same(location.name, name))) {
    errors.name = `${siteName} already has a location called "${name}"`;
  }
  if (!form.kind) errors.kind = 'Choose what kind of location it is';
  return errors;
}

export const toLocationInput = (form: LocationForm): StorageLocationFormInput => ({ name: form.name.trim(), type: form.kind });

// --- bin ------------------------------------------------------------------------------------

export interface BinForm {
  code: string;
  materialId: string;
  owner: string;
  minQuantity: string;
  maxQuantity: string;
  unitCost: string;
}

export type BinFormErrors = Partial<Record<keyof BinForm, string>>;

export function binFormFrom(bin: StorageBin | null, siteOwner: string, suggestedCode: string): BinForm {
  if (!bin) {
    return { code: suggestedCode, materialId: '', owner: siteOwner === 'Unknown' ? '' : siteOwner, minQuantity: '', maxQuantity: '', unitCost: '' };
  }
  return {
    code: bin.code,
    materialId: bin.material?.id ?? '',
    owner: bin.owner ?? '',
    minQuantity: bin.minQuantity === null ? '' : String(bin.minQuantity),
    maxQuantity: bin.maxQuantity === null ? '' : String(bin.maxQuantity),
    unitCost: bin.unitCost === null ? '' : String(bin.unitCost),
  };
}

/**
 * Why a bin's material cannot be changed right now, or null when it can. Stock that is there
 * cannot be relabelled as another material; the bin has to be counted to zero first.
 */
export function materialLockReason(bin: Pick<StorageBin, 'quantity' | 'material'> | null): string | null {
  if (!bin || !bin.material || bin.quantity === 0) return null;
  return 'The bin still holds stock. Count it to zero before changing its material.';
}

export function validateBinForm(
  form: BinForm,
  location: Pick<StorageLocation, 'name' | 'bins'>,
  editingId: string | null
): BinFormErrors {
  const errors: BinFormErrors = {};
  const code = form.code.trim();
  if (!code) errors.code = 'Enter a bin code';
  else if (code.length > BIN_CODE_MAX_LENGTH) errors.code = `Use ${BIN_CODE_MAX_LENGTH} characters or fewer`;
  else if (location.bins.some((bin) => bin.id !== editingId && same(bin.code, code))) {
    errors.code = `${location.name} already has a bin called "${code}"`;
  }

  const min = parseNumber(form.minQuantity);
  const max = parseNumber(form.maxQuantity);
  const cost = parseNumber(form.unitCost);
  if (min === 'invalid' || (min !== null && min < 0)) errors.minQuantity = 'A number, 0 or more';
  if (max === 'invalid' || (max !== null && max < 0)) errors.maxQuantity = 'A number, 0 or more';
  if (!errors.minQuantity && !errors.maxQuantity && min !== null && max !== null && (min as number) > (max as number)) {
    errors.maxQuantity = 'The maximum must not be below the minimum';
  }
  if (cost === 'invalid' || (cost !== null && cost < 0)) errors.unitCost = 'A cost, 0 or more';
  return errors;
}

/** Only call once validateBinForm is clean. The cost is left out unless the caller may set it. */
export function toBinInput(form: BinForm, includeCost: boolean): BinFormInput {
  return {
    code: form.code.trim(),
    materialId: form.materialId || null,
    owner: textOrNull(form.owner),
    minQuantity: parseNumber(form.minQuantity) as number | null,
    maxQuantity: parseNumber(form.maxQuantity) as number | null,
    ...(includeCost ? { unitCost: parseNumber(form.unitCost) as number | null } : {}),
  };
}
